import { atom, type Atom, type PrimitiveAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls/web-storage-service';

import { inferenceAppIdsFixture, inferenceAppsByIdFixture } from '../fixtures';
import type { InferenceApp, InferenceAppId, InferenceAppsStatus } from '../types';

const mockModeStorage = {
	getItem: (key: string) => AtlBrowserStorageLocal.getItem(key),
	setItem: (key: string, value: string) =>
		AtlBrowserStorageLocal.setStrictlyNecessaryItem(key, value),
	removeItem: (key: string) => AtlBrowserStorageLocal.removeItem(key),
};

/**
 * jotai store for the GraphClaw Hub, modeled on `inferred-projects/state/atoms.ts`:
 * normalized primitive atoms seeded from fixtures, plus derived read-only atoms.
 */

// --- Primitive atoms (writable), seeded from fixtures ---

export const InferenceAppsByIdAtom: PrimitiveAtom<Record<InferenceAppId, InferenceApp>> =
	atom<Record<InferenceAppId, InferenceApp>>(inferenceAppsByIdFixture);

export const InferenceAppIdsAtom: PrimitiveAtom<InferenceAppId[]> =
	atom<InferenceAppId[]>(inferenceAppIdsFixture);

/** Whether the "Add inference app" modal is open. */
export const IsAddModalOpenAtom: PrimitiveAtom<boolean> = atom<boolean>(false);

/** Loading lifecycle for the mocked list fetch. Starts as `loading`. */
export const InferenceAppsStatusAtom: PrimitiveAtom<InferenceAppsStatus> =
	atom<InferenceAppsStatus>('loading');

/** Whether the local V2 preview uses mock catalog and app data, persisted across refreshes. */
export const IsGraphClawHubMockEnabledAtom: PrimitiveAtom<boolean> = atomWithStorage<boolean>(
	'dtac-graphclaw-hub-mock-mode-enabled',
	false,
	createJSONStorage<boolean>(() => mockModeStorage),
	{ getOnInit: true },
);

/** Whether mock mode also seeds the JSM incident report app and its reports. */
export const IsGraphClawHubMockJsmFlowEnabledAtom: PrimitiveAtom<boolean> =
	atomWithStorage<boolean>(
		'dtac-graphclaw-hub-mock-jsm-flow-enabled',
		false,
		createJSONStorage<boolean>(() => mockModeStorage),
		{ getOnInit: true },
	);

/** Number of V1 or V2 app changes that have not finished yet. */
export const GraphClawHubActiveMutationCountAtom: PrimitiveAtom<number> = atom<number>(0);

// --- Derived (read-only) atoms ---

/** The inference apps in display order, resolved from the id list + by-id map. */
export const InferenceAppsListAtom: Atom<InferenceApp[]> = atom<InferenceApp[]>((get) => {
	const byId = get(InferenceAppsByIdAtom);
	return get(InferenceAppIdsAtom)
		.map((id) => byId[id])
		.filter((app): app is InferenceApp => Boolean(app));
});

export const InferenceAppsCountAtom: Atom<number> = atom<number>(
	(get) => get(InferenceAppIdsAtom).length,
);
